import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Zap, Award, Factory, FileText, CheckCircle2, 
  ChevronRight, ChevronLeft, Send, Check, User, Mail, Phone,
  Loader2, XCircle
} from 'lucide-react';
import { Button } from '../ui/button'; 
import { cn } from '../../lib/utils'; 
import { SurveyController } from './useGreenSurvyController';
import { PersonalInfo, SurveyData } from './model/survey.model';

const SECTIONS = [
  {
    id: 'A',
    title: 'Organizational Profile',
    icon: Building2,
    description: 'Tell us about your organization type and consumption scale.',
    questions: [
      {
        id: 'q1',
        text: 'Organization Category',
        type: 'radio',
        options: [
          'Manufacturing', 'Infrastructure / EPC', 'IT / ITES / Data Center',
          'Port / Logistics', 'Healthcare / Education', 'Commercial Real Estate', 'Other'
        ]
      },
      {
        id: 'q2',
        text: 'Approximate Annual Electricity Consumption (in Units)',
        type: 'range',
        min: 0,
        max: 100000,
        step: 1000,
        options: []
      },
      {
        id: 'q3',
        text: 'Electricity Procurement Sources (Select all that apply)',
        type: 'checkbox',
        options: [
          'DISCOM supply', 'Rooftop solar', 'Captive / Group captive',
          'Open Access renewable', 'Market instruments (RECs / others)'
        ]
      }
    ]
  },
  {
    id: 'B',
    title: 'Green Energy Tariff',
    icon: Zap,
    description: 'Awareness regarding Green Energy Tariff options.',
    questions: [
      {
        id: 'q4',
        text: 'Are you aware of Green Energy Tariff options offered by DISCOMs?',
        type: 'radio',
        options: ['Yes – fully aware', 'Somewhat aware', 'Heard of it but not sure', 'Not aware']
      },
      {
        id: 'q5',
        text: 'Would you consider opting for a Green Energy Tariff?',
        type: 'radio',
        options: ['Already opted', 'Yes, immediately', 'Yes, after cost evaluation', 'Maybe in future', 'Not interested']
      },
      {
        id: 'q6',
        text: 'What would motivate you to opt for Green Energy Tariff? (Select all that apply)',
        type: 'checkbox',
        options: [
          'Regulatory compliance', 'Sustainability goals', 'Investor expectations',
          'Access to green finance', 'Brand & reputation', 'Cost predictability'
        ]
      }
    ]
  },
  {
    id: 'C',
    title: 'Renewable Certificates',
    icon: Award,
    description: 'Understanding Renewable Energy Consumption Certificates.',
    questions: [
      {
        id: 'q7',
        text: 'Are you aware of Renewable Energy Consumption Certificates?',
        type: 'radio',
        options: ['Fully aware', 'Somewhat aware', 'Heard of it', 'Not aware']
      },
      {
        id: 'q8',
        text: 'How important is a globally verifiable certificate for you?',
        type: 'radio',
        options: ['Very important', 'Important', 'Neutral', 'Not important']
      },
      {
        id: 'q9',
        text: 'Where would you use such certificates? (Select all that apply)',
        type: 'checkbox',
        options: [
          'SEBI BRSR / Sustainability reports', 'ESG disclosures',
          'Global supply chain audits', 'Internal reporting', 'Not required'
        ]
      }
    ]
  },
  {
    id: 'D',
    title: 'Scope 2 & 3 Emissions',
    icon: Factory,
    description: 'Knowledge about emission scopes and impacts.',
    questions: [
      {
        id: 'q10',
        text: 'Are you familiar with Scope 2 emissions?',
        type: 'radio',
        options: ['Yes – clearly understood', 'Somewhat understood', 'Heard of it', 'Not aware']
      },
      {
        id: 'q11',
        text: 'Are you familiar with Scope 3 emissions?',
        type: 'radio',
        options: ['Yes – clearly understood', 'Somewhat understood', 'Heard of it', 'Not aware']
      },
      {
        id: 'q12',
        text: 'Do you agree: "Sourcing decisions directly impact Scope 2 disclosures"?',
        type: 'radio',
        options: ['Strongly agree', 'Agree', 'Neutral', 'Disagree']
      },
      {
        id: 'q13',
        text: 'Do customers/lenders ask for emissions data?',
        type: 'radio',
        options: ['Yes – frequently', 'Occasionally', 'Rarely', 'Never']
      }
    ]
  },
  {
    id: 'E',
    title: 'ESG Readiness',
    icon: FileText,
    description: 'Current reporting status and challenges.',
    questions: [
      {
        id: 'q14',
        text: 'Does your organization currently report under any framework?',
        type: 'checkbox',
        options: ['SEBI BRSR', 'Annual Sustainability Report', 'Global frameworks (GRI/CDP)', 'Not currently reporting']
      },
      {
        id: 'q15',
        text: 'Key challenges in sustainability reporting? (Select all that apply)',
        type: 'checkbox',
        options: ['Data availability', 'Traceability', 'Assurance readiness', 'Internal coordination', 'Cost concerns']
      }
    ]
  },
  {
    id: 'F',
    title: 'Sustainable Finance',
    icon: CheckCircle2,
    description: 'Link between sustainability and finance options.',
    questions: [
      {
        id: 'q16',
        text: 'Are you aware sustainability can improve finance access?',
        type: 'radio',
        options: ['Yes – fully aware', 'Somewhat aware', 'Heard of it', 'Not aware']
      },
      {
        id: 'q17',
        text: 'Would green finance influence your energy decisions?',
        type: 'radio',
        options: ['Yes – significantly', 'Yes – moderately', 'Neutral', 'No']
      },
      {
        id: 'q18',
        text: 'Which benefits are most relevant? (Select all that apply)',
        type: 'checkbox',
        options: ['Lower interest rates', 'Lender confidence', 'Global competitiveness', 'Risk mitigation', 'Regulatory preparedness']
      }
    ]
  },
  {
    id: 'G',
    title: 'Transition Interest',
    icon: Send,
    description: 'Interest in structured green energy transition.',
    questions: [
      {
        id: 'q19',
        text: 'Interested in a structured transition framework?',
        type: 'radio',
        options: ['Yes – immediately', 'Yes – exploratory discussion', 'Maybe later', 'Not interested']
      },
      {
        id: 'q20',
        text: 'What support would you value most? (Select all that apply)',
        type: 'checkbox',
        options: [
          'Energy accounting', 'Green energy sourcing', 'Renewable certification',
          'Sustainability reporting', 'End-to-end compliance'
        ]
      }
    ]
  }
];

export const GreenEnergySurvey: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0); // 0 = personal info, 1+ = sections
  const [answers, setAnswers] = useState<SurveyData>({});
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    name: '',
    email: '',
    mobile: ''
  });
  const [errors, setErrors] = useState<Partial<PersonalInfo>>({});
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [useOtherForQ2, setUseOtherForQ2] = useState<boolean>(false);

  const totalSteps = SECTIONS.length + 1; // +1 for personal info step
  const isPersonalInfoStep = currentStep === 0;
  const currentSectionIndex = currentStep - 1;
  const currentSection = isPersonalInfoStep ? null : SECTIONS[currentSectionIndex];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const validatePersonalInfo = (): boolean => {
    const newErrors: Partial<PersonalInfo> = {};
    
    if (!personalInfo.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Email is optional - no validation
    
    if (!personalInfo.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(personalInfo.mobile.replace(/\D/g, ''))) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOptionSelect = (qId: string, option: string, type: string): void => {
    setAnswers((prev) => {
      if (type === 'radio') {
        return { ...prev, [qId]: option };
      } else {
        // Checkbox logic
        const currentList = (prev[qId] as string[]) || [];
        if (currentList.includes(option)) {
          return { ...prev, [qId]: currentList.filter((item) => item !== option) };
        } else {
          return { ...prev, [qId]: [...currentList, option] };
        }
      }
    });
  };

  const handleNumberInput = (qId: string, value: string): void => {
    setAnswers((prev) => ({ ...prev, [qId]: value }));
  };

  const handleNext = (): void => {
    if (isPersonalInfoStep) {
      if (validatePersonalInfo()) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (currentStep < totalSteps - 1) {
      // No validation for sections - just move to next
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Last section - submit directly without validation
      handleFinish();
    }
  };

  const handleBack = (): void => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = async (): Promise<void> => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await SurveyController.submitSurvey(personalInfo, answers);
      
      if (response.success) {
        console.log("Survey Submitted Successfully:", response);
        setIsCompleted(true);
      } else {
        setSubmitError(response.message || 'Failed to submit survey. Please try again.');
      }
    } catch (error) {
      console.error("Survey submission error:", error);
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePersonalInfoChange = (field: keyof PersonalInfo, value: string): void => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (isCompleted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center">
        <div className="bg-white p-10 rounded-2xl shadow-lg border border-gray-100 max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600">
            Your responses have been recorded. We appreciate your contribution towards a sustainable energy future.
          </p>
          <Button 
            className="mt-8 bg-green-600 hover:bg-green-700 text-white"
            onClick={() => window.location.reload()}
          >
            Submit Another Response
          </Button>
        </div>
      </div>
    );
  }

  // Personal Info Step
  if (isPersonalInfoStep) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4 font-sans">
        <div className="max-w-3xl mx-auto">
          
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Green Energy Readiness Survey</h1>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              Help us design a transparent and globally verifiable energy transition pathway.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="bg-white rounded-full h-2 mb-8 overflow-hidden shadow-sm">
            <motion.div 
              className="h-full bg-green-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative min-h-[500px] flex flex-col">
            
            {/* Section Header */}
            <div className="bg-green-50/50 p-6 border-b border-green-100 flex items-start gap-4">
              <div className="p-3 bg-white rounded-xl shadow-sm border border-green-100">
                <User className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Personal Information
                </h2>
                <p className="text-sm text-gray-500 mt-1">Please provide your contact details to get started.</p>
              </div>
            </div>

            {/* Form Area */}
            <div className="p-6 md:p-8 flex-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key="personal-info"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Name Field */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 font-semibold text-gray-800 text-base">
                      <User className="w-4 h-4 text-green-600" />
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={personalInfo.name}
                      onChange={(e) => handlePersonalInfoChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className={cn(
                        "w-full p-3 rounded-lg border text-sm transition-all duration-200 outline-none",
                        errors.name 
                          ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-200"
                          : "border-gray-200 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      )}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 font-semibold text-gray-800 text-base">
                      <Mail className="w-4 h-4 text-green-600" />
                      Email Address <span className="text-gray-400 text-sm font-normal">(Optional)</span>
                    </label>
                    <input
                      type="email"
                      value={personalInfo.email}
                      onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                      placeholder="Enter your email address"
                      className={cn(
                        "w-full p-3 rounded-lg border text-sm transition-all duration-200 outline-none",
                        errors.email 
                          ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-200"
                          : "border-gray-200 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      )}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Mobile Field */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 font-semibold text-gray-800 text-base">
                      <Phone className="w-4 h-4 text-green-600" />
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={personalInfo.mobile}
                      onChange={(e) => handlePersonalInfoChange('mobile', e.target.value)}
                      placeholder="Enter your 10-digit mobile number"
                      className={cn(
                        "w-full p-3 rounded-lg border text-sm transition-all duration-200 outline-none",
                        errors.mobile 
                          ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-200"
                          : "border-gray-200 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      )}
                    />
                    {errors.mobile && (
                      <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t border-gray-100 flex justify-end bg-gray-50">
              <Button
                onClick={handleNext}
                className="px-8 bg-green-600 hover:bg-green-700 !text-white shadow-md shadow-green-200 flex items-center"
              >
                <span className="text-gray font-medium">Next Step</span>
                <ChevronRight className="w-4 h-4 ml-2 text-gray" />
              </Button>
            </div>

          </div>
          
          <p className="text-center text-xs text-gray-400 mt-6">
            Step {currentStep + 1} of {totalSteps}
          </p>

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 font-sans">
      <div className="max-w-3xl mx-auto">
        
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Green Energy Readiness Survey</h1>
          <p className="text-gray-500 text-sm max-w-xl mx-auto">
            Help us design a transparent and globally verifiable energy transition pathway.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-full h-2 mb-8 overflow-hidden shadow-sm">
          <motion.div 
            className="h-full bg-green-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative min-h-[600px] flex flex-col">
          
          {/* Section Header */}
          <div className="bg-green-50/50 p-6 border-b border-green-100 flex items-start gap-4">
            <div className="p-3 bg-white rounded-xl shadow-sm border border-green-100">
              {currentSection && React.createElement(currentSection.icon, { className: "w-6 h-6 text-green-600" })}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Section {currentSection?.id}: {currentSection?.title}
              </h2>
              <p className="text-sm text-gray-500 mt-1">{currentSection?.description}</p>
            </div>
          </div>

          {/* Question Area */}
          <div className="p-6 md:p-8 flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                {currentSection?.questions.map((q, idx) => {
                  return (
                  <div key={q.id} className="space-y-3">
                    <h3 className="font-semibold text-base flex items-start gap-2 text-gray-800">
                      <span>Q{idx + 1 + (calculateQuestionOffset(currentSectionIndex))}. {q.text}</span>
                    </h3>
                    
                    {/* Range Slider for Q2 */}
                    {q.type === 'range' ? (
                      <div className="space-y-4">
                        {/* Toggle between Slider and Other */}
                        <div className="flex gap-4">
                          <div
                            onClick={() => {
                              setUseOtherForQ2(false);
                              // Reset to slider default if switching from Other
                              if (useOtherForQ2) {
                                handleNumberInput(q.id, '0');
                              }
                            }}
                            className={cn(
                              "cursor-pointer px-4 py-2 rounded-lg border text-sm transition-all duration-200 flex items-center gap-2",
                              !useOtherForQ2
                                ? "bg-green-50 border-green-500 text-green-900 font-medium shadow-sm ring-1 ring-green-500"
                                : "bg-white border-gray-200 text-gray-600 hover:border-green-300 hover:bg-gray-50"
                            )}
                          >
                            <div className={cn(
                              "w-4 h-4 rounded-full border flex items-center justify-center",
                              !useOtherForQ2 ? "border-green-600" : "border-gray-300"
                            )}>
                              {!useOtherForQ2 && <div className="w-2 h-2 rounded-full bg-green-600" />}
                            </div>
                            Use Slider
                          </div>
                          <div
                            onClick={() => {
                              setUseOtherForQ2(true);
                              handleNumberInput(q.id, '');
                            }}
                            className={cn(
                              "cursor-pointer px-4 py-2 rounded-lg border text-sm transition-all duration-200 flex items-center gap-2",
                              useOtherForQ2
                                ? "bg-green-50 border-green-500 text-green-900 font-medium shadow-sm ring-1 ring-green-500"
                                : "bg-white border-gray-200 text-gray-600 hover:border-green-300 hover:bg-gray-50"
                            )}
                          >
                            <div className={cn(
                              "w-4 h-4 rounded-full border flex items-center justify-center",
                              useOtherForQ2 ? "border-green-600" : "border-gray-300"
                            )}>
                              {useOtherForQ2 && <div className="w-2 h-2 rounded-full bg-green-600" />}
                            </div>
                            Other
                          </div>
                        </div>

                        {!useOtherForQ2 ? (
                          /* Slider Input */
                          <div className="max-w-xl space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-500">0</span>
                              <span className="text-lg font-bold text-green-600">
                                {parseInt((answers[q.id] as string) || '0').toLocaleString()} Units
                              </span>
                              <span className="text-sm text-gray-500">100,000</span>
                            </div>
                            <input
                              type="range"
                              min={(q as any).min || 0}
                              max={(q as any).max || 100000}
                              step={(q as any).step || 1000}
                              value={(answers[q.id] as string) || '0'}
                              onChange={(e) => handleNumberInput(q.id, e.target.value)}
                              className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600 
                                [&::-webkit-slider-thumb]:appearance-none 
                                [&::-webkit-slider-thumb]:w-6 
                                [&::-webkit-slider-thumb]:h-6 
                                [&::-webkit-slider-thumb]:rounded-full 
                                [&::-webkit-slider-thumb]:bg-green-600 
                                [&::-webkit-slider-thumb]:cursor-pointer
                                [&::-webkit-slider-thumb]:shadow-lg
                                [&::-webkit-slider-thumb]:border-2
                                [&::-webkit-slider-thumb]:border-white
                                [&::-moz-range-thumb]:w-6 
                                [&::-moz-range-thumb]:h-6 
                                [&::-moz-range-thumb]:rounded-full 
                                [&::-moz-range-thumb]:bg-green-600 
                                [&::-moz-range-thumb]:cursor-pointer
                                [&::-moz-range-thumb]:border-2
                                [&::-moz-range-thumb]:border-white"
                              style={{
                                background: `linear-gradient(to right, #22c55e 0%, #22c55e ${(parseInt((answers[q.id] as string) || '0') / 100000) * 100}%, #e5e7eb ${(parseInt((answers[q.id] as string) || '0') / 100000) * 100}%, #e5e7eb 100%)`
                              }}
                            />
                            <div className="flex justify-between text-xs text-gray-400">
                              <span>Step: 1,000 units</span>
                            </div>
                          </div>
                        ) : (
                          /* Other - Custom Input */
                          <div className="max-w-md">
                            <input
                              type="number"
                              min={0}
                              value={(answers[q.id] as string) || ''}
                              onChange={(e) => handleNumberInput(q.id, e.target.value)}
                              placeholder="Enter your custom value"
                              className={cn(
                                "w-full p-3 rounded-lg border text-sm transition-all duration-200 outline-none",
                                "border-gray-200 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-100"
                              )}
                            />
                            <p className="text-xs text-gray-400 mt-2">
                              Enter any value (no limit)
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {q.options.map((opt) => {
                          const isSelected = q.type === 'radio' 
                            ? answers[q.id] === opt
                            : (answers[q.id] as string[])?.includes(opt);

                          return (
                            <div
                              key={opt}
                              onClick={() => handleOptionSelect(q.id, opt, q.type)}
                              className={cn(
                                "cursor-pointer p-3 rounded-lg border text-sm transition-all duration-200 flex items-center gap-3",
                                isSelected 
                                  ? "bg-green-50 border-green-500 text-green-900 font-medium shadow-sm ring-1 ring-green-500"
                                  : "bg-white border-gray-200 text-gray-600 hover:border-green-300 hover:bg-gray-50"
                              )}
                            >
                              <div className={cn(
                                "w-5 h-5 flex items-center justify-center border transition-all flex-shrink-0",
                                q.type === 'radio' ? 'rounded-full' : 'rounded-md',
                                isSelected 
                                  ? "border-green-600 bg-white" 
                                  : "border-gray-300 bg-white"
                              )}>
                                {isSelected && (
                                  q.type === 'radio' ? (
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-600" />
                                  ) : (
                                    <Check className="w-3.5 h-3.5 text-green-600 stroke-[3px]" />
                                  )
                                )}
                              </div>
                              <span>{opt}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Submit Error Message */}
          {submitError && (
            <div className="mx-6 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-700 text-sm font-medium">Submission Failed</p>
                <p className="text-red-600 text-xs mt-1">{submitError}</p>
              </div>
              <button 
                onClick={() => setSubmitError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Footer Actions */}
          <div className="p-6 border-t border-gray-100 flex justify-between bg-gray-50">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0 || isSubmitting}
              className="px-6 border-gray-300 text-gray-600 hover:bg-white disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4 mr-2" /> Back
            </Button>

            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              className="px-8 bg-green-600 hover:bg-green-700 !text-white shadow-md shadow-green-200 flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span className="font-medium">Submitting...</span>
                </>
              ) : (
                <>
                  <span className="text-gray font-medium">
                    {currentStep === totalSteps - 1 ? 'Submit Survey' : 'Next Step'}
                  </span>
                  {currentStep !== totalSteps - 1 && <ChevronRight className="w-4 h-4 ml-2 text-gray" />}
                </>
              )}
            </Button>
          </div>

        </div>
        
        <p className="text-center text-xs text-gray-400 mt-6">
          Step {currentStep + 1} of {totalSteps}
        </p>

      </div>
    </div>
  );
};

// Helper to number questions continuously across sections
function calculateQuestionOffset(stepIndex: number): number {
  let count = 0;
  for (let i = 0; i < stepIndex; i++) {
    count += SECTIONS[i].questions.length;
  }
  return count;
}

export default GreenEnergySurvey;